import { Rng } from '../combat/rng';

/**
 * Hand-drawn primitives for Canvas2D. Every shape is drawn as slightly
 * wobbly, double-passed pencil strokes, so art generated with a different
 * seed looks like the same doodle re-traced — which is how "line boil"
 * frames are produced.
 */

export const INK = {
  graphite: '#3b3b3b',
  soft: '#6b6b6b',
  paper: '#fbf8ee',
  lineBlue: '#a9c6e8',
  margin: '#e88a8a',
  red: '#e4572e',
  orange: '#f3a712',
  yellow: '#f6d55c',
  green: '#6ab04c',
  teal: '#3caea3',
  blue: '#2e86de',
  purple: '#8e5bd6',
  pink: '#ff8fab',
  brown: '#9c6644',
  black: '#222222',
  white: '#ffffff',
} as const;

export interface PenOptions {
  color?: string;
  width?: number;
  /** Max perpendicular wobble in px. */
  wobble?: number;
  /** Number of overlapping pencil passes. */
  passes?: number;
  alpha?: number;
}

export class Sketch {
  readonly rng: Rng;

  constructor(
    readonly ctx: CanvasRenderingContext2D,
    seed: number,
  ) {
    this.rng = new Rng(seed);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  }

  private j(amount: number): number {
    return (this.rng.float() * 2 - 1) * amount;
  }

  /** A pencil line between two points. */
  line(x1: number, y1: number, x2: number, y2: number, o: PenOptions = {}): void {
    const { color = INK.graphite, width = 3, wobble = 1.6, passes = 2, alpha = 0.92 } = o;
    const { ctx } = this;
    for (let p = 0; p < passes; p++) {
      ctx.strokeStyle = color;
      ctx.globalAlpha = p === 0 ? alpha : alpha * 0.45;
      ctx.lineWidth = p === 0 ? width : Math.max(1, width * 0.6);
      const mx = (x1 + x2) / 2 + this.j(wobble * 1.5);
      const my = (y1 + y2) / 2 + this.j(wobble * 1.5);
      ctx.beginPath();
      ctx.moveTo(x1 + this.j(wobble), y1 + this.j(wobble));
      ctx.quadraticCurveTo(mx, my, x2 + this.j(wobble), y2 + this.j(wobble));
      ctx.stroke();
    }
    ctx.globalAlpha = 1;
  }

  /** Open or closed polyline through points with smooth wobbly joins. */
  path(points: readonly [number, number][], closed: boolean, o: PenOptions = {}): void {
    const { color = INK.graphite, width = 3, wobble = 1.4, passes = 2, alpha = 0.92 } = o;
    const { ctx } = this;
    if (points.length < 2) return;
    for (let p = 0; p < passes; p++) {
      ctx.strokeStyle = color;
      ctx.globalAlpha = p === 0 ? alpha : alpha * 0.4;
      ctx.lineWidth = p === 0 ? width : Math.max(1, width * 0.55);
      const pts = points.map(([x, y]) => [x + this.j(wobble), y + this.j(wobble)] as [number, number]);
      if (closed) {
        // Overshoot the closing point a little, like a real pencil does.
        const first = pts[0]!;
        const second = pts[1]!;
        pts.push([first[0] + (second[0] - first[0]) * 0.25, first[1] + (second[1] - first[1]) * 0.25]);
      }
      ctx.beginPath();
      ctx.moveTo(pts[0]![0], pts[0]![1]);
      for (let i = 1; i < pts.length - 1; i++) {
        const [x, y] = pts[i]!;
        const [nx, ny] = pts[i + 1]!;
        ctx.quadraticCurveTo(x, y, (x + nx) / 2, (y + ny) / 2);
      }
      const last = pts[pts.length - 1]!;
      ctx.lineTo(last[0], last[1]);
      ctx.stroke();
    }
    ctx.globalAlpha = 1;
  }

  ellipsePoints(cx: number, cy: number, rx: number, ry: number, segments = 18, noise = 0.06): [number, number][] {
    const pts: [number, number][] = [];
    const start = this.rng.float() * Math.PI * 2;
    for (let i = 0; i < segments; i++) {
      const a = start + (i / segments) * Math.PI * 2;
      const n = 1 + this.j(noise);
      pts.push([cx + Math.cos(a) * rx * n, cy + Math.sin(a) * ry * n]);
    }
    return pts;
  }

  ellipse(cx: number, cy: number, rx: number, ry: number, o: PenOptions & { fill?: string } = {}): void {
    const pts = this.ellipsePoints(cx, cy, rx, ry);
    if (o.fill) this.crayonFill(pts, o.fill);
    this.path(pts, true, o);
  }

  poly(points: [number, number][], o: PenOptions & { fill?: string } = {}): void {
    if (o.fill) this.crayonFill(points, o.fill);
    this.path(points, true, o);
  }

  rect(x: number, y: number, w: number, h: number, o: PenOptions & { fill?: string } = {}): void {
    this.poly(
      [
        [x, y],
        [x + w, y],
        [x + w, y + h],
        [x, y + h],
      ],
      o,
    );
  }

  /** Colored-pencil hatching clipped to a polygon, slightly outside the lines. */
  crayonFill(points: readonly [number, number][], color: string, density = 5): void {
    const { ctx } = this;
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const [x, y] of points) {
      minX = Math.min(minX, x);
      minY = Math.min(minY, y);
      maxX = Math.max(maxX, x);
      maxY = Math.max(maxY, y);
    }
    ctx.save();
    ctx.beginPath();
    const ox = this.j(2);
    const oy = this.j(2);
    points.forEach(([x, y], i) => (i === 0 ? ctx.moveTo(x + ox, y + oy) : ctx.lineTo(x + ox, y + oy)));
    ctx.closePath();
    ctx.globalAlpha = 0.28;
    ctx.fillStyle = color;
    ctx.fill();
    ctx.clip();
    ctx.strokeStyle = color;
    ctx.lineWidth = 2.2;
    const angle = -0.9 + this.j(0.15);
    const dx = Math.cos(angle);
    const dy = Math.sin(angle);
    const diag = Math.hypot(maxX - minX, maxY - minY);
    const cx = (minX + maxX) / 2;
    const cy = (minY + maxY) / 2;
    for (let t = -diag / 2; t < diag / 2; t += density + this.j(1.2)) {
      // Lines perpendicular offset along (-dy, dx).
      const px = cx - dy * t;
      const py = cy + dx * t;
      ctx.globalAlpha = 0.5 + this.rng.float() * 0.3;
      ctx.beginPath();
      ctx.moveTo(px - dx * diag, py - dy * diag);
      ctx.lineTo(px + dx * diag, py + dy * diag);
      ctx.stroke();
    }
    ctx.restore();
    ctx.globalAlpha = 1;
  }

  dot(x: number, y: number, r: number, color: string = INK.graphite): void {
    const { ctx } = this;
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.arc(x + this.j(0.6), y + this.j(0.6), r, 0, Math.PI * 2);
    ctx.fill();
  }

  /** Short stroke scribble used for hair, grass, motion marks. */
  scribble(x: number, y: number, w: number, h: number, loops: number, o: PenOptions = {}): void {
    const pts: [number, number][] = [];
    for (let i = 0; i <= loops * 6; i++) {
      const t = i / (loops * 6);
      pts.push([x + t * w + Math.sin(t * loops * Math.PI * 2) * (w / loops / 2), y + Math.cos(t * loops * Math.PI * 2) * h]);
    }
    this.path(pts, false, { width: 2, ...o });
  }

  star(cx: number, cy: number, r: number, o: PenOptions & { fill?: string } = {}): void {
    const pts: [number, number][] = [];
    for (let i = 0; i < 10; i++) {
      const a = -Math.PI / 2 + (i * Math.PI) / 5;
      const rr = i % 2 === 0 ? r : r * 0.45;
      pts.push([cx + Math.cos(a) * rr, cy + Math.sin(a) * rr]);
    }
    this.poly(pts, { wobble: 0.8, ...o });
  }

  text(str: string, x: number, y: number, size: number, color: string = INK.graphite, rotate = 0): void {
    const { ctx } = this;
    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(rotate + this.j(0.03));
    ctx.fillStyle = color;
    ctx.font = `${size}px "Gochi Hand", "Patrick Hand", "Itim", cursive`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(str, 0, 0);
    ctx.restore();
  }
}

/** Cheap paper-grain speckle so canvas art sits on the page instead of floating. */
export function grain(ctx: CanvasRenderingContext2D, w: number, h: number, seed: number, amount = 0.04): void {
  const rng = new Rng(seed);
  ctx.save();
  ctx.globalAlpha = amount;
  ctx.fillStyle = '#000';
  const count = Math.floor((w * h) / 90);
  for (let i = 0; i < count; i++) ctx.fillRect(rng.float() * w, rng.float() * h, 1, 1);
  ctx.restore();
}
